// Strips characters not allowed in text inputs.
// Allowed: alphanumeric, Latin diacritics (é ñ ø etc.), @ - ' . and spaces.
// Spaces are trimmed on blur, not on every keystroke (so mid-word spaces work).
const ALLOWED = /[^a-zA-Z0-9\u00C0-\u024F @'.\-+,\/_]/g;

export function sanitizeInput(value: string): string {
    return value.replace(ALLOWED, "");
}

export function trimInput(value: string): string {
    return value.trim();
}

// ── Phone ─────────────────────────────────────────────────────────────────────
// Normalises a single phone number.
// Strips formatting characters; converts "00XX…" international prefix to "+XX…".
// Numbers with an explicit "+" prefix or local "0…" numbers are kept as-is.
export function normalizePhone(raw: string): string {
    const s = raw.replace(/[\s\-().\/]/g, "");
    if (!s) return "";
    if (s.startsWith("00")) return "+" + s.slice(2);
    return s;
}

// Formats a phone number nicely for display (apply on blur).
// Stored/emitted values remain clean (no spaces); this is display-only.
//
// German rules (DIN 5008):
//   +49 mobile  (15x/16x/17x): "+49 170 1234567"
//   +49 landline:               "+49 30 12345678"
//   local mobile (015x/016x/017x): "0170 1234567"
//   local landline:                "030 12345678"
export function formatPhone(raw: string): string {
    const s = normalizePhone(raw);
    if (!s) return "";

    if (s.startsWith("+49")) {
        const local = s.slice(3).replace(/\D/g, "");
        if (!local) return "+49";
        return "+49 " + formatDE(local);
    }

    if (s.startsWith("+")) {
        // Generic international: keep as-is (already normalised)
        return s;
    }

    // Local number starting with 0
    const digits = s.replace(/\D/g, "");
    if (/^(015|016|017)/.test(digits)) {
        // Mobile local: 4-digit prefix + subscriber
        return digits.slice(0, 4) + (digits.length > 4 ? " " + digits.slice(4) : "");
    }
    if (digits.startsWith("0") && digits.length > 3) {
        // Landline local: 3-digit area code + subscriber
        return digits.slice(0, 3) + " " + digits.slice(3);
    }

    return s;
}

// Formats the LOCAL part of a +49 number (digits after "+49", no trunk 0).
// Called by PhoneInput on blur when Germany is selected.
export function formatDE(digits: string): string {
    if (!digits) return "";
    if (/^(15|16|17)/.test(digits)) {
        // Mobile: 3-digit prefix + subscriber  →  "170 1234567"
        return digits.slice(0, 3) + (digits.length > 3 ? " " + digits.slice(3) : "");
    }
    // Landline: 2-digit area code + subscriber  →  "30 12345678"
    return digits.slice(0, 2) + (digits.length > 2 ? " " + digits.slice(2) : "");
}

// Strips any country-code prefix a user might type into the local field when
// +49 is already shown in the dial selector, then removes the trunk zero.
// Examples:
//   "0049123456"      → "123456"
//   "00490160789456"  → "160789456"  (0049 stripped, leading 0 stripped)
//   "01701234567"     → "1701234567" (leading trunk 0 stripped)
//   "1701234567"      → "1701234567" (already clean)
export function normalizeDeLocal(raw: string): string {
    let digits = raw.replace(/\D/g, "");
    // Strip 0049 international dialing prefix
    if (digits.startsWith("0049")) digits = digits.slice(4);
    // Strip +49 typed into the field (already converted to digits, so just "49" at start
    // but only when followed by a known non-zero German subscriber prefix to avoid
    // ambiguity with area codes starting with 49)
    else if (digits.startsWith("49") && /^49(1[5-7]|[2-9])/.test(digits)) digits = digits.slice(2);
    // Strip single trunk zero (German local dialing prefix)
    if (digits.startsWith("0")) digits = digits.slice(1);
    return digits;
}

// Validates the normalised local part of a +49 number.
// Returns an error string or null if valid.
export function validateDeLocal(digits: string): string | null {
    if (!digits) return null; // empty is OK (field is optional)
    if (digits.length < 3)  return "Nummer zu kurz";
    if (digits.length > 12) return "Nummer zu lang";
    // Mobile numbers (15x / 16x / 17x): must be exactly 10 or 11 digits
    if (/^(15|16|17)/.test(digits) && (digits.length < 10 || digits.length > 11)) {
        return "Mobilnummer: 10–11 Ziffern erwartet";
    }
    return null;
}

// Normalises a comma-separated list of phone numbers.
export function normalizePhoneList(raw: string): string {
    return raw
        .split(",")
        .map((p) => normalizePhone(p.trim()))
        .filter(Boolean)
        .join(", ");
}

// ── PLZ ───────────────────────────────────────────────────────────────────────
// Restricts input to at most 5 digits (German postal code format).
export function normalizePlz(raw: string): string {
    return raw.replace(/\D/g, "").slice(0, 5);
}

// ── IBAN ──────────────────────────────────────────────────────────────────────
// Uppercases and groups into blocks of 4 (e.g. "DE89 3704 0044 0532 0130 00").
export function normalizeIban(raw: string): string {
    const cleaned = raw.replace(/\s/g, "").toUpperCase();
    return cleaned.match(/.{1,4}/g)?.join(" ") ?? cleaned;
}

// MOD-97 checksum validation (ISO 13616).
// Returns true only if the IBAN is structurally valid AND the check digits are correct.
export function validateIban(raw: string): boolean {
    const iban = raw.replace(/\s/g, "").toUpperCase();
    if (iban.length < 5 || iban.length > 34) return false;
    // Move first 4 chars to the end, then replace each letter with its numeric value (A=10…Z=35)
    const rearranged = iban.slice(4) + iban.slice(0, 4);
    const numeric = rearranged.replace(/[A-Z]/g, (ch) => String(ch.charCodeAt(0) - 55));
    // MOD-97 on a big integer — process in chunks to avoid precision loss
    let remainder = 0;
    for (const ch of numeric) {
        remainder = (remainder * 10 + parseInt(ch, 10)) % 97;
    }
    return remainder === 1;
}
